import React, { FC, useMemo, useState } from "react";
import { LuPencil } from "react-icons/lu";
import { toast } from "react-toastify";

import { Card } from "../../components/card/Card";
import { LottieLoading } from "../../components/graphics/LottieLoading";
import { getLinkedFirm } from "../../sessionStorage/sessionStorage";
import {
  useGetClientTeamQuery,
  useSetClientTeamMutation,
} from "../../slices/ClientTeamSlice";
import { useGetUsersQuery } from "../../slices/UserSlice";
import {
  errorText,
  modalCancel,
  modalPrimary,
  PortalModal,
} from "../clientPortal/PortalModal";
import { ProfileWidgetActionButton } from "../contacts/components/profile";
import { ClientTeamView } from "./clientTeam.types";
import {
  ClientTeamInfoButton,
  ClientTeamPersonRow,
  PersonPicker,
  sortedTeam,
} from "./ClientTeamParts";

/**
 * Client page, left column under the details (owner 2026-10-31): the client's
 * Client owner and Client manager, Edit, and the ⓘ explanation.
 */
export const ClientTeamCard: FC<{ customerId?: string | null }> = ({
  customerId,
}) => {
  const { data, isLoading } = useGetClientTeamQuery(
    { customerId: customerId || "" },
    { skip: !customerId },
  );
  const [editing, setEditing] = useState(false);

  if (!customerId) return null;

  return (
    <Card extraClasses={"overflow-hidden"}>
      <>
        <div className={"min-w-0 px-5 pb-5 pt-5"}>
          <div className={"flex min-w-0 items-center justify-between gap-3"}>
            <div className={"flex min-w-0 items-center gap-1.5"}>
              <span className={"text-[17px] font-extrabold text-[#1F2433]"}>
                Client team
              </span>
              <ClientTeamInfoButton />
            </div>
            {data?.available && (
              <ProfileWidgetActionButton
                icon={<LuPencil size={14} />}
                title={"Edit client team"}
                onClick={() => setEditing(true)}
              />
            )}
          </div>
          <div className={"mt-4 flex flex-col gap-3.5"}>
            {isLoading ? (
              <LottieLoading />
            ) : !data?.available ? (
              <span className={"text-[13px] text-[#6B7185]"}>
                {data?.message ||
                  "The client team isn't available on this server yet."}
              </span>
            ) : (
              <>
                <ClientTeamPersonRow
                  label={"Client owner"}
                  person={data.owner}
                />
                <ClientTeamPersonRow
                  label={"Client manager"}
                  person={data.manager}
                />
              </>
            )}
          </div>
        </div>
        {editing && data?.available && (
          <EditClientTeamModal
            customerId={customerId}
            team={data}
            onClose={() => setEditing(false)}
          />
        )}
      </>
    </Card>
  );
};

export const EditClientTeamModal: FC<{
  customerId: string;
  team: ClientTeamView;
  onClose: () => void;
}> = ({ customerId, team, onClose }) => {
  const orgId = getLinkedFirm()?.orgId || "";
  const { data: users } = useGetUsersQuery(orgId, { skip: !orgId });
  const teamMembers = useMemo(() => sortedTeam(users), [users]);
  const [ownerId, setOwnerId] = useState(team.owner?.id || "");
  const [managerId, setManagerId] = useState(team.manager?.id || "");
  const [save, { isLoading }] = useSetClientTeamMutation();
  const [problem, setProblem] = useState<string | null>(null);

  const onSave = async () => {
    setProblem(null);
    try {
      await save({
        customerId,
        ownerUserId: ownerId || null,
        managerUserId: managerId || null,
      }).unwrap();
      toast.success("Client team saved.");
      onClose();
    } catch (error) {
      setProblem(errorText(error));
    }
  };

  return (
    <PortalModal
      title={"Edit client team"}
      subtitle={"Pick people from your firm's team. One person can be both."}
      onClose={onClose}
      width={480}
      footer={
        <>
          <button type={"button"} className={modalCancel} onClick={onClose}>
            Cancel
          </button>
          <button
            type={"button"}
            className={modalPrimary}
            disabled={isLoading}
            onClick={onSave}
          >
            {isLoading ? "Saving…" : "Save"}
          </button>
        </>
      }
    >
      <div className={"flex items-center gap-1.5 text-[13px] text-[#6B7185]"}>
        <span>What do they get?</span>
        <ClientTeamInfoButton />
      </div>
      <PersonPicker
        id={"client-team-owner"}
        label={"Client owner"}
        hint={
          "Responsible for the relationship. Only told when something goes wrong."
        }
        value={ownerId}
        onChange={setOwnerId}
        users={teamMembers}
        current={team.owner}
      />
      <PersonPicker
        id={"client-team-manager"}
        label={"Client manager"}
        hint={"Gets new work for this client and its day-to-day notifications."}
        value={managerId}
        onChange={setManagerId}
        users={teamMembers}
        current={team.manager}
      />
      {problem && (
        <div
          role={"alert"}
          className={
            "rounded-[9px] border border-[#FDA29B] bg-[#FEF3F2] px-3 py-2 text-[13px] text-[#B42318]"
          }
        >
          {problem}
        </div>
      )}
    </PortalModal>
  );
};
